import axiosInstance from "../api/apiClient";

/*
  تحديث 101 — مركز الشروحات والتدريب: كل نداءات الخادم في مكان واحد.
  التشغيل يتم من رابط موقّع يعيده الخادم (عنصر video لا يرسل ترويسة المصادقة)، والترجمة تُجلب بالمصادقة وتُحوَّل إلى رابط محلي.
*/

export const errorMessage = (e, fallback = "تعذّر تنفيذ الطلب.") =>
  e?.response?.data?.message || e?.response?.data?.title || e?.message || fallback;

export const fetchCatalog = () => axiosInstance.get("training/catalog").then((r) => r.data);
export const fetchVideo = (id) => axiosInstance.get(`training/videos/${id}`).then((r) => r.data);
export const searchTraining = (q, lang, ai = false) =>
  axiosInstance.get("training/search", { params: { q, lang, ai: ai || undefined } }).then((r) => r.data);
export const fetchPageLinks = () => axiosInstance.get("training/page-links").then((r) => r.data || []);
export const fetchForPage = (route) => axiosInstance.get("training/for-page", { params: { route } }).then((r) => r.data || []);
export const saveProgress = (id, body) => axiosInstance.post(`training/videos/${id}/progress`, body).then((r) => r.data);

/** نص الترجمة WebVTT ← رابط محلي (blob) لعنصر track. */
export async function captionsUrl(id, lang, admin = false) {
  const r = await axiosInstance.get(admin ? `training/admin/videos/${id}/captions/${lang}` : `training/videos/${id}/captions/${lang}`, { responseType: "text", transformResponse: (x) => x });
  const text = typeof r.data === "string" ? r.data : "";
  if (!text.trim().startsWith("WEBVTT")) return null;
  return URL.createObjectURL(new Blob([text], { type: "text/vtt" }));
}

// ───────── الإدارة ─────────
export const adminList = () => axiosInstance.get("training/admin/videos").then((r) => r.data);
export const adminVideo = (id) => axiosInstance.get(`training/admin/videos/${id}`).then((r) => r.data);
export const adminCaptionsText = (id, lang) =>
  axiosInstance.get(`training/admin/videos/${id}/captions/${lang}`, { responseType: "text", transformResponse: (x) => x }).then((r) => r.data || "");
export const createVideo = (body) => axiosInstance.post("training/admin/videos", body).then((r) => r.data);
export const updateVideo = (id, body) => axiosInstance.put(`training/admin/videos/${id}`, body).then((r) => r.data);
export const newVersion = (id, body) => axiosInstance.post(`training/admin/videos/${id}/versions`, body).then((r) => r.data);
export const makeCurrent = (id) => axiosInstance.post(`training/admin/videos/${id}/make-current`).then((r) => r.data);
export const setHidden = (id, hidden) => axiosInstance.post(`training/admin/videos/${id}/${hidden ? "hide" : "show"}`).then((r) => r.data);
export const saveChapters = (id, chapters) => axiosInstance.put(`training/admin/videos/${id}/chapters`, chapters).then((r) => r.data);
export const saveCaptions = (id, lang, vtt) => axiosInstance.put(`training/admin/videos/${id}/captions/${lang}`, { vtt }).then((r) => r.data);
export const markReviewed = (id, fingerprints, build) => axiosInstance.post(`training/admin/videos/${id}/reviewed`, { fingerprints, build }).then((r) => r.data);
export const flagVideo = (id, reason) => axiosInstance.post(`training/admin/videos/${id}/flag`, { reason }).then((r) => r.data);
export const checkUpdates = (fingerprints, build) => axiosInstance.post("training/admin/check-updates", { fingerprints, build }).then((r) => r.data);
export const importPackage = (uploadId) => axiosInstance.post("training/admin/import-package", { uploadId }).then((r) => r.data);
export function uploadPoster(id, file) {
  const fd = new FormData();
  fd.append("file", file);
  return axiosInstance.post(`training/admin/videos/${id}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
}

/**
 * رفع ملف كبير على أجزاء (≤ 8MB لكل طلب) مع الاستئناف: عند انقطاع جزء يُعاد من آخر إزاحة استلمها الخادم.
 * onProgress(0..1). يعيد uploadId بعد التحقق من الملف على الخادم.
 */
export async function chunkedUpload(file, kind, onProgress, signal) {
  const start = await axiosInstance.post("training/admin/uploads", { kind, fileName: file.name, size: file.size }).then((r) => r.data);
  const id = start.uploadId;
  const size = Math.min(start.chunkSize || 8 * 1024 * 1024, 8 * 1024 * 1024);
  let offset = 0;
  let failures = 0;
  while (offset < file.size) {
    if (signal?.aborted) throw new Error("أُلغي الرفع.");
    const part = file.slice(offset, offset + size);
    try {
      const r = await axiosInstance.put(`training/admin/uploads/${id}`, part, {
        params: { offset },
        headers: { "Content-Type": "application/octet-stream" },
        transformRequest: [(d) => d],
        signal,
      });
      offset = r.data.received;
      failures = 0;
    } catch (e) {
      const received = e?.response?.data?.received;
      if (typeof received === "number" && e?.response?.status === 409) { offset = received; continue; }
      failures += 1;
      if (failures > 4) throw e;
      const wait = 1500 * failures;
      await new Promise((res) => setTimeout(res, wait));
      const st = await axiosInstance.get(`training/admin/uploads/${id}`).then((r) => r.data).catch(() => null);
      if (st) offset = st.received;
    }
    onProgress?.(Math.min(1, offset / file.size));
  }
  await axiosInstance.post(`training/admin/uploads/${id}/complete`);
  return id;
}

// ───────── أدوات العرض ─────────
export const fmtTime = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const sec = n % 60;
  const p = (x) => String(x).padStart(2, "0");
  return h ? `${h}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
};

/** «07:42» أو «1:02:03» ← ثوانٍ. */
export const parseTime = (t) => {
  const parts = String(t || "").trim().split(":").map((x) => Number(x));
  if (!parts.length || parts.some((x) => Number.isNaN(x))) return null;
  return parts.reduce((acc, x) => acc * 60 + x, 0);
};

/** هل يطابق المسار الفعلي نمط صفحة (يقبل :param)؟ */
export const routeMatches = (pattern, path) => {
  if (!pattern || !path || pattern.length > 200 || !/^\/[A-Za-z0-9/_:-]*$/.test(pattern)) return false;
  const rx = "^" + pattern.replace(/\/+$/, "").split("/").map((p) => (p.startsWith(":") ? "[^/]+" : p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))).join("/") + "/?$";
  return new RegExp(rx, "i").test(path);
};

export const fmtDate = (v) => {
  if (!v) return "—";
  try { const d = new Date(v); return d.toLocaleDateString("en-GB").split("/").reverse().join("-"); } catch { return v; }
};
